<template>
  <div>
    <!-- 右下角AI图标 -->
    <div ref="aiIcon" 
         class="ai-icon" 
         :class="{ 
           'ai-icon-disabled': !isAIChatEnabled,
           'dragging': isDragging 
         }" 
         :style="{ bottom: iconBottom + 'px' }"
         @click="handleIconClick"
         @touchstart.passive="false"
         @touchend.passive="false"
         @contextmenu.prevent>
      <img :src="getCurrentLanguage() === 'en-US' ? require('@/assets/AIUnscramble/ai_icon.en.png') : require('@/assets/AIUnscramble/ai_icon.png')" alt="AI助手" />
      <!-- 禁用状态遮罩 -->
    </div>

    <!-- AI聊天弹出框 -->
    <div v-if="showChat" :class="['ai-chat-dialog', { hiding: isHiding }]" @touchmove.prevent="handleDialogTouchMove"
      @wheel.prevent="handleDialogWheel">
      <div class="dialog-content">
        <!-- 顶部关闭按钮 -->
        <div class="close-btn" @click="closeChat">
          <div class="close-icon"></div>
        </div>

        <!-- 头部AI标识 -->
        <div class="ai-header">
          <div class="ai-avatar">
            <img :src="require('@/assets/AIUnscramble/图标-AI小助手@2x.png')" alt="AI头像" />
          </div>
          <div class="ai-title-container">
            <div class="ai-title">
              <span class="ai-name">{{ $t('aiAssistant.title') }}</span>
              <div class="ai-sound">
                <img :src="soundOn
                  ? require('@/assets/AIUnscramble/Volume-notice.png')
                  : require('@/assets/AIUnscramble/关闭声音.png')
                  " @click="toggleSound" alt="声音图标" />
              </div>
            </div>
            <div class="ai-desc">
              {{ $t('aiAssistant.disclaimer') }}
            </div>
          </div>
        </div>

        <!-- 聊天内容区域 -->
        <div class="chat-content" @touchmove.stop @wheel.stop ref="chatContent">
          <!-- 历史加载中提示 -->
          <div v-if="isLoadingHistory && historyPage === 1" class="history-loading">
            <div class="dot-countdown-timer">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>

          <!-- 没有更多历史提示 -->
          <div v-if="!hasMoreHistory && historyPage > 1" class="history-end-tip">
            {{ $t('aiAssistant.noMoreHistory') }}
          </div>

          <!-- 欢迎消息，始终保留 -->
          <div class="message-row">
            <div class="ai-message">
              <div style="white-space: pre-line">{{ $t('aiAssistant.welcomeMessage') }}</div>
            </div>
          </div>

          <!-- 添加用户消息和AI回复的动态显示 -->
          <div v-for="(message, index) in messages" :key="index" :class="[
            'message-row',
            message.type === 'user' ? 'message-row-user' : 'message-row-ai',
          ]">
            <div :class="[
              message.type === 'user' ? 'user-message' : 'ai-message',
              message.isThinking ? 'thinking-message' : '',
            ]">
              <template v-if="message.isThinking">
                <div class="thinking-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </template>
              <template v-else>
                <div style="white-space: pre-line">{{ message.content }}</div>
              </template>
            </div>
          </div>

          <!-- AI思考中的动画 -->
          <div v-if="isThinking" class="message-row">
            <div class="ai-message thinking">
              <div class="thinking-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="chat-footer">
          <!-- 录音状态提示文字 -->
          <div class="recording-status" v-if="isRecording">
            <span :class="{ cancel: isCancelled }">{{
              isCancelled ? $t('aiAssistant.releaseToCancel') : $t('aiAssistant.releaseToSend')
              }}</span>
          </div>

          <!-- 语音输入区域 -->
          <div v-if="!isKeyboardMode" class="voice-input-area" :class="{ recording: isRecording }">
            <!-- 按住说话按钮 -->
            <div class="voice-button" @touchstart.prevent="startRecording" @touchend.prevent="stopRecording"
              @touchcancel.prevent="stopRecording" @touchmove="handleTouchMove" @mousedown.prevent="startRecording"
              @mouseup="stopRecording" @mouseleave="stopRecording" @selectstart.prevent @contextmenu.prevent
              @click.prevent>
              <div v-if="!isRecording" class="voice-content">
                <span class="voice-text">{{ $t('aiAssistant.holdToSpeak') }}</span>
              </div>
              <div v-else class="recording-indicator">
                <!-- 倒计时显示 -->
                <!-- <div class="recording-content"> -->

                <span class="seconds-font" v-if="countdownSeconds <= 10">{{ $t('aiAssistant.stopRecordingIn', { seconds: countdownSeconds }) }}
                </span>
                <div v-else class="countdown-timer">
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>
                  <div class="span"></div>

                  <!-- </div> -->
                </div>
              </div>
            </div>

            <!-- 键盘图标 -->
            <div class="keyboard-toggle" @click.stop="switchToKeyboardMode">
              <img :src="require('@/assets/AIUnscramble/Keyboard.png')" alt="键盘" />
            </div>
          </div>

          <!-- 键盘输入区域 -->
          <div v-if="isKeyboardMode" class="text-input-container">
            <!-- 使用Flex布局的输入区域容器 -->
            <div class="text-input-area" @click="handleTextAreaClick">
              <!-- 文本输入区域 -->
              <div class="input-wrapper">
                <div class="auto-grow-input" ref="textInput" contenteditable="true" spellcheck="false"
                  autocomplete="off" autocorrect="off" autocapitalize="off" @compositionstart="handleCompositionStart"
                  @compositionend="handleCompositionEnd" @input="handleInput" @keydown="handleKeyDown"
                  @focus="inputFocused = false" @blur="handleInputBlur" :placeholder="$t('aiAssistant.inputPlaceholder')"></div>
              </div>
              <!-- 发送按钮，固定位置 -->
              <div class="input-action" @click="handleActionClick">
                <!-- v-if="!inputText" -->
                <img v-if="!inputText" :src="require('@/assets/AIUnscramble/语音.png')" alt="语音" class="voice-icon" />
                <img v-else :src="require('@/assets/AIUnscramble/send.png')" alt="发送" class="send-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { postAIChatStream } from "@/net/api";
import { getChatHistory } from "@/net/api";
import ContinuousAudioPlayer from "@/utils/AudioContext.js";
import { wxConfig } from "@/utils/useWechatAuth.js";
import { getCurrentLanguage } from "@/i18n/utils.js";
import { DragHelperOptimized } from "@/utils/DragHelperOptimized.js";
// formatMarkdown已移除，保持原始文本格式以确保段落间隔一致性

export default {
  name: "AIChat",
  data() {
    return {
      showChat: false, // 是否显示ai弹出框
      isHiding: false,
      soundOn: true,
      isRecording: false,
      touchStartY: 0,
      isCancelled: false,
      isKeyboardMode: true, //是否键盘输入
      inputText: "",
      inputFocused: false,
      messages: [],
      // isProcessing: false,
      isComposing: false,
      isMultiLine: false,
      isStreaming: false,
      // 历史记录相关
      historyPage: 1,
      historyPageSize: 10,
      isLoadingHistory: false,
      hasMoreHistory: true,
      historyLoaded: false,
      isThinking: false,
      aiInterpretContent: "", // 文本输出
      aiInterpretSummary: "",
      aiInterpretLoading: true,
      eventSource: null,
      isCollecting: true,
      streamReader: null,
      config: {
        sampleRate: 16000,
        channels: 1,
        volume: 1,
        playbackRate: 1.0,
        fadeDuration: 50,
        filterFrequency: 8000,
        smallChunkThreshold: 5000,
      },
      reportPlayer: null,
      countdownSeconds: 60,
      countdownTimer: null,
      packageNo: this.$store.getters.getPackageNo,
      isTokenFinish: true,
      localId: null,
      // 录音取消相关
      touchStartTime: 0, // 按下开始时间
      canCancel: true, // 是否可以取消（用于控制停滞后的行为）
      cancelDelayTimer: null, // 延迟计时器
      recordingStarted: false, // 微信录音是否真正开始
      wxSDKReady: false, // 微信SDK是否已准备好
      // 打字机效果相关
      typewriterQueue: [], // 待显示的token队列
      isTyping: false, // 是否正在打字
      // isAuthorized: false,//是否获取录音权限
      // AI图标拖拽相关
      iconBottom: 120, // 图标距离底部的距离
      dragHelper: null, // 拖拽助手实例
      isDragging: false, // 是否正在拖拽
    };
  },
  watch: {
    "$store.getters.getPackageNo": {
      handler(newVal) {
        this.packageNo = newVal;
      },
      immediate: true,
    },
    "$store.getters.GetOpenInterpret": {
      handler(newVal) {
        if (newVal && this.reportPlayer) {
          if (this.reportPlayer.gainNode) {
            this.reportPlayer.gainNode.gain.value = 0;
          }
        }
      },
      immediate: true,
    },
    // 监听路由变化，重新初始化微信SDK
    // '$route'(to, from) {
    //   if (to.path !== from.path && /micromessenger/i.test(navigator.userAgent)) {
    //     console.log('路由变化，重新初始化微信SDK:', from.path, '->', to.path);
    //     this.reinitWechatSDK();
    //   }
    // },
  },
  computed: {
    // 检查 AI 聊天是否可用
    isAIChatEnabled() {
      return this.$store.getters.isAIChatEnabled;
    },
    isWechatMiniProgram() {
      return /miniprogram/i.test(navigator.userAgent) || window.__wxjs_environment === 'miniprogram';
    },
    isFlutterWebView() {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      return isMobile;
    }
  },
  async mounted() {
    // 初始化拖拽功能
    this.$nextTick(() => {
      this.initDragHelper();
    });
    // 检查是否在小程序webview环境
    const isMiniProgram =
      window.__wxjs_environment === "miniprogram" ||
      /miniprogram/i.test(navigator.userAgent) ||
      window.wx?.miniProgram;

    // 只在微信环境中初始化微信SDK
    if (/micromessenger/i.test(navigator.userAgent)) {
      try {
        // 小程序环境需要延迟初始化
        const initDelay = isMiniProgram ? 1000 : 0;

        setTimeout(async () => {
          await this.initWechatSDK();
        }, initDelay);
      } catch (error) {
        console.error("微信 JSSDK 初始化失败:", error);
        this.$wx = null;
      }
    } else {
      // ("非微信环境，跳过微信SDK初始化");
      this.$wx = null;
    }

    // 如果聊天框已打开，添加滚动监听器
    if (this.showChat && this.$refs.chatContent) {
      this.$refs.chatContent.addEventListener("scroll", this.handleScroll);
    }
  },
  beforeDestroy() {
    // 清理拖拽助手
    if (this.dragHelper) {
      this.dragHelper.destroy();
      this.dragHelper = null;
    }
    // 移除滚动监听器
    if (this.$refs.chatContent) {
      this.$refs.chatContent.removeEventListener("scroll", this.handleScroll);
    }

    // 清理流数据读取器
    if (this.streamReader) {
      this.streamReader.cancel();
      this.streamReader = null;
    }

    // 清理音频播放器
    if (this.reportPlayer) {
      this.reportPlayer.cleanup();
      this.reportPlayer = null;
    }

    // 清理打字机效果
    this.cleanupTypewriter();

    // 清理定时器
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    // 如果正在录音，停止录音
    if (
      this.recordingStarted &&
      this.$wx &&
      typeof this.$wx.stopRecord === "function"
    ) {
      this.$wx.stopRecord({
        success: () => console.log("组件销毁时停止录音成功"),
        fail: () => console.log("组件销毁时停止录音失败"),
      });
    }
    if (this.isFlutterWebView && window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('cancelNativeRecording');
    }
    // 清理录音相关状态
    this.resetRecordingState();
  },
  methods: {
    // 获取当前语言
    getCurrentLanguage,
    // AI图标点击处理
    handleIconClick(e) {
      // 如果正在拖拽，不处理点击
      if (this.isDragging) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      
      // 处理正常点击
      this.toggleChat();
    },
    // 初始化拖拽功能
    initDragHelper() {
      if (this.$refs.aiIcon && !this.dragHelper) {
        this.dragHelper = new DragHelperOptimized(this.$refs.aiIcon, {
          axis: 'y', // 只允许垂直拖拽
          threshold: 12, // 优化拖拽阈值
          bounds: {
            top: -(window.innerHeight * 0.6), // 上边界：屏幕高度的60%
            bottom: 50 // 下边界：离底部50px
          },
          onStart: () => {
            this.isDragging = false;
          },
          onMove: (data) => {
            if (data.hasMoved && !this.isDragging) {
              this.isDragging = true;
            }
            if (this.isDragging) {
              // 使用requestAnimationFrame优化渲染性能
              requestAnimationFrame(() => {
                const maxBottom = 120 + (window.innerHeight * 0.6);
                const newBottom = Math.max(50, Math.min(maxBottom, 120 - data.y));
                this.iconBottom = newBottom;
              });
            }
          },
          onEnd: (data) => {
            if (data.hasMoved) {
              // 有移动，完成拖拽
              this.isDragging = false;
            } else if (!data.shouldPreventClick) {
              // 没有移动且不需要阻止点击，触发点击事件
              this.isDragging = false;
              // 使用微任务确保状态更新完成
              Promise.resolve().then(() => {
                if (!this.isDragging) {
                  this.toggleChat();
                }
              });
            } else {
              // 需要阻止点击（如长按）
              this.isDragging = false;
            }
          }
        });
      }
    },
    // 初始化微信SDK
    async initWechatSDK() {
      try {
        // 执行微信配置
        await wxConfig((wx) => {
          this.$wx = wx;
          this.wxSDKReady = true;
          this.setupWechatCallbacks();
        });
      } catch (error) {
        console.error("微信SDK初始化失败:", error);
        this.wxSDKReady = false;
        this.$wx = null;
      }
    },

    // 设置微信回调
    setupWechatCallbacks() {
      if (!this.$wx) return;
      // 在微信SDK初始化完成后设置录音结束回调
      if (typeof this.$wx.onVoiceRecordEnd === "function") {
        this.$wx.onVoiceRecordEnd({
          complete: (res) => {
            if (!this.isRecording && !this.recordingStarted) {
              // ("未在录音状态");
              return;
            }
            this.localId = res.localId;
            this.resetRecordingState();
            // ("录音超时，自动停止");
            this.processWechatRecording(res.localId);
          },
        });
      }
    },
    //  打开ai小助手
    toggleChat() {
      // 检查 AI 聊天是否可用
      if (!this.isAIChatEnabled) {
        this.$showToast(this.$t('aiAssistant.waitForAIComplete'));
        return;
      }
      if (this.showChat) {
        this.closeChat();
      } else {
        this.showChat = true;
        // 关闭音频ai解读音频
        this.$store.commit("SetOpenAiChat", { openAiChat: true });
        // 控制打开弹窗
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";

        this.$nextTick(() => {
          // ("对话框已打开，初始化输入区域");

          if (this.isKeyboardMode && this.$refs.textInput) {
            // ("初始化键盘输入区域");
            this.initInputArea();
          } else {
            // ("初始化语音输入区域");
            const voiceInputArea = this.$el.querySelector(".voice-input-area");
            if (voiceInputArea) {
              // voiceInputArea.style.height = "46px";
              // ("直接设置语音输入区域高度为60px");
            } else {
              console.warn("未找到语音输入区域元素");
            }

            setTimeout(() => {
              this.initVoiceArea();
            }, 100);
          }

          if (this.$refs.chatContent) {
            this.$refs.chatContent.addEventListener(
              "scroll",
              this.handleScroll
            );

            // 重置分页参数，准备加载最新的聊天记录
            this.historyPage = 1;
            this.hasMoreHistory = true;
            this.historyLoaded = false;

            // 初始加载最新一页的聊天记录
            this.loadChatHistory()
              .then(() => {
                this.$nextTick(() => {
                  this.scrollToBottom();
                });
              })
              .catch((err) => {
                console.error("初始加载聊天记录失败:", err);
                this.scrollToBottom();
              });
          }
        });
      }
    },
    // 关闭ai小助手
    closeChat() {
      // 停止流式数据收集

      // this.stopInterpretation();

      // 清理音频播放器资源
      // if (this.reportPlayer) {
      //   this.reportPlayer.pause();
      //   // this.reportPlayer = null;
      // }

      // 停止所有进行中的进程
      // this.stopAllProcesses();

      // 重置相关状态
      // this.isProcessing = false;

      // 如果正在录音，停止录音
      if (
        this.recordingStarted &&
        this.$wx &&
        typeof this.$wx.stopRecord === "function"
      ) {
        this.$wx.stopRecord({
          success: () => console.log("关闭聊天时停止录音成功"),
          fail: () => console.log("关闭聊天时停止录音失败"),
        });
      }

      this.resetRecordingState();
      this.isStreaming = false;
      
      // 清理打字机效果
      this.cleanupTypewriter();
      
      this.showChat = false;
      this.$store.commit("SetOpenAiChat", { openAiChat: false });
      this.isHiding = true;
      setTimeout(() => {
        // this.showChat = false;
        this.isHiding = false;
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";

        if (this.$refs.chatContent) {
          this.$refs.chatContent.removeEventListener(
            "scroll",
            this.handleScroll
          );
        }
      }, 300);
    },
    // 处理动作按钮点击
    handleActionClick() {
      // 获取最新的输入文本状态
      const hasText = this.inputText && this.inputText.trim().length > 0;

      if (hasText) {
        // 如果有文本，则发送文本
        this.sendTextMessage();
      } else {
        // 如果没有文本，则切换回语音模式
        this.switchToVoiceMode();
        // this.$showToast("请先输入问题");
      }
    },
    /**
     * 发送文本消息
     */
    sendTextMessage() {
      if (!this.$refs.textInput) return;
      if (!this.isTokenFinish) {
        this.$showToast(this.$t('aiAssistant.responseNotComplete'));
        return;
      }

      // 获取输入文本
      const text = this.$refs.textInput.innerText.trim();

      // 如果文本为空，则不处理
      if (!text) {
        return;
      }

      // 添加用户消息到消息列表
      this.messages.push({
        type: "user",
        content: text,
      });

      // 清空输入框
      this.$refs.textInput.innerHTML = "";
      this.inputText = "";
      this.resetInputArea();

      // 滚动到底部，确保新消息可见
      this.scrollToBottom();

      // 发送文本消息到服务器
      this.startInterpretation(text);
      // this.fetchAIInterpretData(text);
      // this.sendTextToServer(text);
    },
    initReportPlayer() {
      // 如果已有播放器且正在播放，则停止它
      if (this.reportPlayer && this.reportPlayer.isPlaying) {
        this.reportPlayer.stop();
      }

      this.reportPlayer = new ContinuousAudioPlayer({
        sampleRate: this.config.sampleRate,
        channels: this.config.channels,
        volume: this.config.volume,
        playbackRate: this.config.playbackRate,
        fadeDuration: this.config.fadeDuration,
        filterFrequency: this.config.filterFrequency,
        smallChunkThreshold: this.config.smallChunkThreshold,
        onStatusUpdate: (status) => {
          // 只有在播放完成时才切换到重播图标，不包括初始化时的"已停止"状态
          if (status === "播放完成") {
            // this.soundOn = 0; // 切换到重播图标
          }
        },
        onPlaybackComplete: () => {
          // 播放完成回调
          // this.soundOn = 0; // 切换到重播图标
        },
      });

      return this.reportPlayer;
    },

    base64ToArrayBuffer(base64) {
      const binaryStr = atob(base64);
      const len = binaryStr.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      return bytes.buffer;
    },
    processFlutterRecording(blob) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Audio = reader.result.split(',')[1]; // 获取 Base64 数据
        // 添加到消息列表或发送到服务器
        this.messages.push({ type: 'user', content: '语音消息: ' + base64Audio });
        this.scrollToBottom();
        // 调用 AI 接口处理语音
        this.startInterpretation(base64Audio);
      };
      reader.readAsDataURL(blob);
    },
    decodePcmData(arrayBuffer) {
      try {
        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          console.warn("收到空的音频数据");
          return null;
        }

        const int16Array = new Int16Array(arrayBuffer);
        const floatArray = new Float32Array(int16Array.length);

        const compressionThreshold = 0.6;
        const compressionRatio = 0.7;

        for (let i = 0; i < int16Array.length; i++) {
          let sample = int16Array[i] / 32768.0;

          if (Math.abs(sample) > compressionThreshold) {
            const excess = Math.abs(sample) - compressionThreshold;
            const compression = excess * (1 - compressionRatio);
            sample =
              sample > 0
                ? compressionThreshold + compression
                : -compressionThreshold - compression;
          }

          floatArray[i] = sample * 0.85;
        }

        return floatArray;
      } catch (err) {
        console.error("解码 PCM 数据失败:", err);
        return null;
      }
    },
    // 发送文本消息，获取音频流
    async startInterpretation(text) {
      // 先停止之前的流数据收集
      // this.stopInterpretation();
      if (this.streamReader) {
        await this.streamReader.cancel();
        this.streamReader = null;
      }
      const formData = new FormData();
      formData.append("serial_number", this.packageNo);
      // formData.append("content", text);
      formData.append("content", text);
      this.aiInterpretContent = "";
      this.aiInterpretSummary = "";
      this.aiInterpretLoading = true;
      this.isCollecting = true;
      this.isTokenFinish = false;
      this.initReportPlayer();
      // 在用户交互的同步上下文中初始化音频
      this.reportPlayer.initAudioContext();

      if (this.eventSource) {
        this.eventSource.close();
      }
      // 添加思考中的消息
      this.messages.push({
        type: "ai",
        content: "",
        isThinking: true,
      });
      this.scrollToBottom();
      try {
        const response = await postAIChatStream(formData);

        try {
          // 先尝试直接读取JSON
          const data = await response.clone().json(); // 使用clone()以防body已被使用
          if (data.errorCode !== undefined && data.errorCode !== 0) {
            this.messages.pop()
            this.messages.pop()
            this.$showToast(data.message);
            this.isTokenFinish = true;
            return
          }
        } catch (error) {
          console.log('响应不是JSON或已处理，尝试流式读取');
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error("ReadableStream not supported in this browser.");
        }

        this.aiInterpretLoading = false;

        this.streamReader = response.body.getReader();
        const decoder = new TextDecoder();
        let partialData = "";
        // tokenData变量已移除，直接使用打字机效果处理token

        while (this.isCollecting) {
          try {
            const { done, value } = await this.streamReader.read();

            if (done) {
              // ("流数据读取完成");
              break;
            }

            const textData = decoder.decode(value, { stream: true });
            const dataChunks = (partialData + textData).split("\n");
            partialData = dataChunks.pop() || "";

            for (const chunk of dataChunks) {
              if (!chunk.trim()) continue;

              try {
                // 处理 Server-Sent Events 格式的数据
                let jsonString = chunk;

                // 如果是 SSE 格式，提取 data: 后面的内容
                if (chunk.startsWith("data: ")) {
                  jsonString = chunk.substring(6); // 移除 "data: " 前缀
                }

                // 跳过空数据或注释行
                if (!jsonString.trim() || jsonString.startsWith(":")) {
                  continue;
                }

                const jsonData = JSON.parse(jsonString);
                const { token, audio, error, token_finish } = jsonData;
                if (token_finish) {
                  this.isTokenFinish = true;
                  // 等待打字机效果完成
                  this.waitForTypingComplete();
                }
                // 文字没有完整之前需要一直滚动到底部
                if (!this.isTokenFinish) {
                  this.scrollToBottom();
                }
                if (error) {
                  // this.messages[this.messages.length-1].isThinking= false;
                  this.messages[this.messages.length - 1].content =
                    "很抱歉未能回答（网络或问题限制）。建议：\n 1. 稍后重试 ​\n​2. 检查网络​ \n​3. 换种提问方式 \n" +
                    (error.message ? "4. " + error.message : "");
                  this.isTokenFinish = true;
                  return;
                }
                if (token) {
                  // 移除思考状态
                  this.messages[this.messages.length - 1].isThinking = false;
                  
                  // 使用打字机效果逐字显示token
                  this.typewriterEffect(token);
                }

                if (audio && this.soundOn) {
                  const arrayBuffer = this.base64ToArrayBuffer(audio);
                  const pcmData = this.decodePcmData(arrayBuffer);

                  if (pcmData) {
                    this.reportPlayer.addPCMData(pcmData);
                  }
                }
              } catch (e) {
                console.error("解析数据失败:", e, "原始数据:", chunk);
                // 不要在这里设置 isCollecting = false，继续处理其他数据
              }
            }
          } catch (readError) {
            console.error("读取流数据失败:", readError);
            break;
          }
        }

        // 处理剩余的部分数据
        if (partialData.trim()) {
          try {
            let jsonString = partialData;
            if (partialData.startsWith("data: ")) {
              jsonString = partialData.substring(6);
            }

            if (jsonString.trim() && !jsonString.startsWith(":")) {
              const jsonData = JSON.parse(jsonString);
              const { token, summarize, audio, token_finish } = jsonData;

              if (token) {
                // 移除思考状态
                this.messages[this.messages.length - 1].isThinking = false;
                
                // 使用打字机效果逐字显示token
                this.typewriterEffect(token);
              }
              if (summarize) this.aiInterpretSummary += summarize;
              if (token_finish) {
                this.isTokenFinish = true;
                // 等待打字机效果完成
                this.waitForTypingComplete();
              }
              if (audio && this.soundOn) {
                // Handle audio playback if needed
              }
            }
          } catch (e) {
            console.error("处理剩余数据失败:", e);
          }
        }
        if (!this.showChat && this.reportPlayer) {
          // this.reportPlayer.stop();
          this.reportPlayer = null;
        }
      } catch (error) {
        console.error("启动解读失败:", error);
        this.aiInterpretLoading = false;
      } finally {
        this.isCollecting = false;
      }
    },
    // 停止流数据收集
    stopInterpretation() {
      // this.isCollecting = false;
      if (this.streamReader) {
        this.streamReader.cancel();
        this.streamReader = null;
      }
    },

    // 打字机效果 - 将新token添加到队列
    typewriterEffect(newToken) {
      // 将新token添加到队列
      this.typewriterQueue.push(newToken);
      
      // 开发环境调试日志
      if (process.env.NODE_ENV === 'development') {
        console.log('收到token:', newToken, '队列长度:', this.typewriterQueue.length);
      }
      
      // 如果没有在打字，开始处理队列
      if (!this.isTyping) {
        this.processTypewriterQueue();
      }
    },

    // 处理打字机队列
    processTypewriterQueue() {
      if (this.typewriterQueue.length === 0) {
        this.isTyping = false;
        if (process.env.NODE_ENV === 'development') {
          console.log('打字机队列处理完成');
        }
        return;
      }

      this.isTyping = true;
      const token = this.typewriterQueue.shift();
      const lastMessage = this.messages[this.messages.length - 1];

      if (process.env.NODE_ENV === 'development') {
        console.log('开始处理token:', token, '剩余队列长度:', this.typewriterQueue.length);
      }

      // 逐字显示当前token
      let charIndex = 0;
      const typeInterval = setInterval(() => {
        if (charIndex < token.length) {
          lastMessage.content += token[charIndex];
          charIndex++;
          this.scrollToBottom();
        } else {
          clearInterval(typeInterval);
          // 继续处理队列中的下一个token
          setTimeout(() => {
            this.processTypewriterQueue();
          }, 10);
        }
      }, 20); // 每20ms显示一个字符
    },

    // 等待打字机效果完成
    waitForTypingComplete() {
      const checkInterval = setInterval(() => {
        if (!this.isTyping && this.typewriterQueue.length === 0) {
          clearInterval(checkInterval);
          // 只标记为完成状态，不应用markdown格式，保持原始换行符
          const lastMessage = this.messages[this.messages.length - 1];
          lastMessage.isCompleted = true;
          // 保持原始内容，不进行格式化，这样间隔和逐字渲染时一致
          
          if (process.env.NODE_ENV === 'development') {
            console.log('打字机效果完成，消息内容:', lastMessage.content);
          }
        }
      }, 100);
    },

    // 清理打字机效果资源
    cleanupTypewriter() {
      this.typewriterQueue = [];
      this.isTyping = false;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('打字机效果资源已清理');
      }
    },

    // 停止当前对话和打字机效果
    stopCurrentConversation() {
      // 停止流数据收集
      this.isCollecting = false;
      
      // 清理流数据读取器
      if (this.streamReader) {
        this.streamReader.cancel();
        this.streamReader = null;
      }
      
      // 清理打字机效果
      this.cleanupTypewriter();
      
      // 设置token完成状态
      this.isTokenFinish = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('当前对话已停止');
      }
    },
    // 切换是否静音播放
    toggleSound() {
      this.soundOn = !this.soundOn;

      // 控制音频播放器
      if (this.reportPlayer) {
        if (this.soundOn) {
          // 如果音频播放器有gainNode，恢复音量
          if (this.reportPlayer.gainNode) {
            this.reportPlayer.gainNode.gain.value =
              this.reportPlayer.volume || 1;
          }
        } else {
          // 关闭声音 - 将音量设为0实现静音效果，而不是暂停播放
          if (this.reportPlayer.gainNode) {
            this.reportPlayer.gainNode.gain.value = 0;
          }
        }
      }
    },

    // 检查微信录音功能是否可用
    isWechatRecordAvailable() {
      const isWechat = /micromessenger/i.test(navigator.userAgent);
      const hasWxSDK = this.$wx && typeof this.$wx.startRecord === "function";
      const isMiniProgram =
        window.__wxjs_environment === "miniprogram" ||
        /miniprogram/i.test(navigator.userAgent) ||
        window.wx?.miniProgram;

      // 小程序环境需要额外检查
      if (isMiniProgram && !this.wxSDKReady) {
        // ("小程序环境下微信SDK未就绪");
        return false;
      }

      return isWechat && hasWxSDK && this.wxSDKReady;
    },

    // 重置录音状态
    resetRecordingState() {
      // ("重置录音状态");
      this.isRecording = false;
      this.recordingStarted = false;
      this.isCancelled = false;
      this.canCancel = true;
      this.touchStartTime = 0;

      // 清理倒计时
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.countdownSeconds = 60;
      }

      // 清理取消延迟计时器
      if (this.cancelDelayTimer) {
        clearTimeout(this.cancelDelayTimer);
        this.cancelDelayTimer = null;
      }
    },

    // 开始录音
    async startRecording(e) {
      if (!this.isTokenFinish) {
        this.$showToast(this.$t('aiAssistant.responseNotComplete'));
        return;
      }

      // 安全地阻止事件冒泡
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }
      if (e && typeof e.preventDefault === "function") {
        e.preventDefault();
      }

      // 确保事件来源是录音按钮区域
      const voiceButton = e.currentTarget || e.target.closest('.voice-button');
      if (!voiceButton) {
        console.warn('录音事件来源不正确');
        return;
      }

      // 记录触摸开始位置和时间
      if (e.touches && e.touches[0]) {
        this.touchStartY = e.touches[0].clientY;
      } else if (e.clientY) {
        this.touchStartY = e.clientY;
      }

      this.touchStartTime = Date.now();
      this.canCancel = true;

      // console.log(
      //   `触摸开始位置: ${this.touchStartY}, 开始时间: ${this.touchStartTime}`
      // );

      // 如果已经在录音或正在处理中，则不执行
      if (this.isRecording) {
        // ("已在录音状态，忽略重复请求");
        return;
      }

      // 立即设置录音状态
      this.isRecording = true;
      this.recordingStarted = false; // 重置微信录音开始状态
      // ("录音状态已设置为 true");

      // 设置延迟计时器，1秒后禁用取消功能
      this.cancelDelayTimer = setTimeout(() => {
        this.canCancel = false;
        // ("停滞时间超过1秒，禁用取消功能");
      }, 1000);
      const isWechat = /micromessenger/i.test(navigator.userAgent);
      console.log('isWechat', isWechat);
      // 检查是否在Flutter WebView环境中
      if (!isWechat && this.isFlutterWebView) {
        try {
          console.log('进来了=====》');
          // 调用Flutter开始录音
          await window.flutter_inappwebview.callHandler('startNativeRecording');
          this.recordingStarted = true;
          this.startCountdown(); // 如果有倒计时逻辑
          // 可选：静音其他音频
          if (this.reportPlayer && this.reportPlayer.gainNode) {
            this.reportPlayer.gainNode.gain.value = 0;
          }
        } catch (error) {
          console.error('启动Flutter录音失败:', error);
          this.$showToast(this.$t('aiAssistant.recordingStartFailed') + error);
          this.resetRecordingState();
        }
      } else {
        // 原有微信或其他录音逻辑
        // ... existing code ...
        // ("startRecording 被调用");


        // 检查微信录音功能是否可用
        if (!this.isWechatRecordAvailable()) {
          // 在微信环境下，尝试重新初始化SDK
          if (isWechat && !this.wxSDKReady) {
            console.log('录音功能不可用，尝试重新初始化微信SDK');
            try {
              await this.initWechatSDK();
              // 等待初始化完成后重新检查
              if (this.isWechatRecordAvailable()) {
                // 重新调用录音方法
                this.startRecording(e);
                return;
              }
            } catch (error) {
              console.error('重新初始化微信SDK失败:', error);
            }
          }
          
          const isMiniProgram =
            window.__wxjs_environment === "miniprogram" ||
            /miniprogram/i.test(navigator.userAgent) ||
            window.wx?.miniProgram;

          if (!isWechat) {
            this.$showToast(`${this.isFlutterWebView}`);
            console.log('当前环境不是微信', navigator.mediaDevices.getUserMedia);
            if (this.isFlutterWebView) {
              if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.$showToast(`${navigator.mediaDevices}`);
                // 回退方案：使用Flutter桥接
                if (window.flutter_inappwebview) {
                  window.flutter_inappwebview.callHandler('startNativeRecording').then((result) => {
                    // 处理原生录音结果
                    console.log('Native recording started:', result);
                  });
                } else {
                  alert('语音功能在当前环境中不可用，请检查权限或更新应用。');
                }
                return;
              }
              try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.mediaRecorder = new MediaRecorder(stream);
                this.mediaRecorder.start();
                this.recordingChunks = [];
                this.mediaRecorder.ondataavailable = (e) => {
                  this.recordingChunks.push(e.data);
                };
                this.isRecording = true;
              } catch (error) {
                console.error('getUserMedia error:', error);
                alert('无法访问麦克风，请检查iOS权限设置。');
              }
            }
          } else if (!this.wxSDKReady) {
            if (isMiniProgram) {
              this.$showToast(this.$t('aiAssistant.miniProgramInitializing'));
              // 在小程序环境下，尝试重新初始化
              setTimeout(() => {
                this.initWechatSDK();
              }, 1000);
            } else {
              this.$showToast(this.$t('aiAssistant.wechatInitializing'));
            }
          } else if (!this.$wx) {
            this.$showToast(this.$t('aiAssistant.wechatSDKInitFailed'));
          } else {
            this.$showToast(this.$t('aiAssistant.wechatVersionNotSupported'));
          }
          this.resetRecordingState();
          return;
        } else if (!isWechat && this.isFlutterWebView) {
          // Flutter WebView 使用 MediaRecorder
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.mediaRecorder = new MediaRecorder(stream);
            this.mediaRecorder.start();
            this.recordingChunks = [];
            this.mediaRecorder.ondataavailable = (e) => {
              this.recordingChunks.push(e.data);
            };
            // 处理录音开始
            this.isRecording = true;
            // 启动倒计时等
          } catch (error) {
            console.error('录音权限错误:', error);
            this.$showToast(this.$t('aiAssistant.grantMicrophonePermission'));
          }
        }

        // 使用微信录音
        this.$wx.startRecord({
          success: () => {
            // 只有在用户还在按住时才设置为真正开始
            if (this.isRecording) {
              this.recordingStarted = true;
              this.startCountdown();
              if (this.reportPlayer) {
                if (this.reportPlayer.gainNode) {
                  this.reportPlayer.gainNode.gain.value = 0;
                }
              }
            } else {
              // 如果用户已经松手，立即停止录音
              this.$wx.stopRecord({
                success: () => console.log("已停止录音"),
                fail: () => console.log("停止录音失败"),
              });
            }
          },
          cancel: () => {
            this.$showToast(this.$t('aiAssistant.microphonePermissionRequired'));
            this.resetRecordingState();
          },
          fail: () => {
            // 只有在用户还在按住时才显示错误提示
            if (this.isRecording) {
              this.$showToast(this.$t('aiAssistant.recordingStartFailed'));
            }
            this.resetRecordingState();
          },
        });
      }

    },
    // 结束录音
    async stopRecording(e) {
      console.log('stopRecording-------------------------');
      // 防止事件冒泡
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }

      // 确保事件来源是录音按钮区域
      const voiceButton = e.currentTarget || e.target.closest('.voice-button');
      if (!voiceButton) {
        console.warn('停止录音事件来源不正确');
        return;
      }

      // 如果没有在录音，则不处理
      if (!this.isRecording) {
        // "未在录音状态，忽略停止录音请求"
        return;
      }
      if (this.countdownSeconds >= 60) {
        this.$showToast(this.$t('aiAssistant.speakingTimeTooShort'));
        this.isCancelled = true;

      }
      // 清理倒计时
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.countdownSeconds = 60;
      }

      this.isRecording = false;

      // 无论环境，先检查取消状态
      if (this.isCancelled) {
        console.log('录音被取消，不进行处理');
        this.isCancelled = false;

        if (this.isFlutterWebView && window.flutter_inappwebview) {
          try {
            await window.flutter_inappwebview.callHandler('cancelNativeRecording');
            console.log('Flutter取消录音成功');
          } catch (error) {
            console.error('Flutter取消录音失败:', error);
          }
        } else if (this.recordingStarted && this.$wx && typeof this.$wx.stopRecord === 'function') {
          this.$wx.stopRecord({
            success: () => console.log('微信取消录音成功'),
            fail: (err) => console.error('微信取消录音失败:', err),
          });
        }

        this.resetRecordingState();
        return; // 确保返回，不执行后续逻辑
      }

      // 非取消情况，处理录音停止
      if (this.isFlutterWebView && window.flutter_inappwebview) {
        try {
          // 新增：延迟 0.5s 再执行
          await new Promise(resolve => setTimeout(resolve, 500));
          const result = await window.flutter_inappwebview.callHandler('stopNativeRecording');
          console.log('Flutter STT结果:', result, this.lastResult);
          //将这次的result缓存下来，然后比对上次与本次是否相同，如果相同，不处理
          if (result === this.lastResult) {
            return;
          } else {
            if (result) {
              this.lastResult = result;
              this.messages.push({ type: 'user', content: result });
              this.startInterpretation(result);
            }
          }
        } catch (error) {
          console.error('停止Flutter录音失败:', error);
          this.$showToast(this.$t('aiAssistant.recordingProcessFailed'));
        }
      } else if (this.recordingStarted && this.$wx && typeof this.$wx.stopRecord === 'function') {
        this.$wx.stopRecord({
          success: (res) => {
            this.localId = res.localId;
            console.log('微信录音结束，localId:', res.localId);
            this.recordingStarted = false;
            this.processWechatRecording(res.localId);
          },
          fail: (err) => {
            console.error('停止微信录音失败:', err);
            this.resetRecordingState();
            this.$showToast(this.$t('aiAssistant.recordingStopFailed'));
          },
        });
      } else {
        console.log('当前环境不支持录音停止');
        this.$showToast(this.$t('aiAssistant.recordingNotSupported'));
      }

      this.resetRecordingState();
    },
    startCountdown() {
      // 清除可能存在的定时器
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.countdownSeconds = 60;
      }

      this.countdownTimer = setInterval(() => {
        this.countdownSeconds -= 1;

        // 当倒计时结束时，自动停止录音并发送
        if (this.countdownSeconds <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.countdownSeconds = 60;
          if (this.isRecording) {
            // "计时器停止");
            this.stopRecording();
          }

          // 设置isCancelled为false确保消息会被发送
          this.isCancelled = false;

          // this.stopRecording({
          //   stopPropagation: () => {},
          //   preventDefault: () => {}
          // });
        }
      }, 1000);
    },
    // 处理触摸移动 - 用于取消录音
    handleTouchMove(e) {
      // 安全地阻止事件冒泡
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      // 确保事件来源是录音按钮区域
      const voiceButton = e.currentTarget || e.target.closest('.voice-button');
      if (!voiceButton) {
        return;
      }

      // 如果没有在录音，则不处理
      if (!this.isRecording) return;

      // 获取当前触摸位置
      let currentY;
      if (e.touches && e.touches[0]) {
        currentY = e.touches[0].clientY;
      } else if (e.clientY) {
        currentY = e.clientY;
      } else {
        return;
      }

      // 计算移动距离
      const moveDistance = this.touchStartY - currentY;

      // 如果上移超过50px，显示取消提示
      if (moveDistance > 50) {
        if (!this.isCancelled) {
          this.isCancelled = true;
          // ("录音设置为取消状态");
        }
      } else {
        if (this.isCancelled) {
          this.isCancelled = false;
          // ("录音取消状态已重置");
        }
      }
    },
    /**
     * 切换到键盘输入模式
     * 功能：从语音模式切换到键盘文本输入模式，并初始化输入框
     */
    switchToKeyboardMode(e) {
      // 阻止事件冒泡和默认行为
      e.stopPropagation();
      e.preventDefault();

      // 如果正在录音，先停止录音
      if (
        this.isRecording &&
        this.recordingStarted &&
        this.$wx &&
        typeof this.$wx.stopRecord === "function"
      ) {
        this.$wx.stopRecord({
          success: () => console.log("切换键盘模式时停止录音成功"),
          fail: () => console.log("切换键盘模式时停止录音失败"),
        });
      }

      // 重置录音状态
      this.resetRecordingState();

      // 切换到键盘输入模式
      this.isKeyboardMode = true;
      // this.isProcessing = false; // 重置处理状态

      // 等待DOM更新后初始化输入框
      this.$nextTick(() => {
        if (this.$refs.textInput) {
          this.initInputArea();
          // 聚焦输入框
          this.$refs.textInput.focus();
        }
      });
    },
    /**
     * 切换到语音模式
     * 功能：从键盘输入模式切换回语音输入模式
     */
    switchToVoiceMode() {
      // 如果正在处理中，则不执行
      // if (this.isProcessing) return;

      // ("切换到语音模式");

      // 重置所有状态
      this.isKeyboardMode = false;
      this.inputText = "";
      this.isMultiLine = false;

      // 重置输入框
      if (this.$refs.textInput) {
        this.$refs.textInput.innerHTML = "";
      }

      // 确保语音输入区域样式正确
      this.$nextTick(() => {
        // 直接设置语音输入区域样式
        const voiceInputArea = this.$el.querySelector(".voice-input-area");
        if (voiceInputArea) {
          // voiceInputArea.style.height = "46px";
          console.log("切换到语音模式：设置高度为60px");
        } else {
          console.warn("切换到语音模式：未找到语音输入区域元素");
        }

        // 再次调用initVoiceArea作为备份
        setTimeout(() => {
          this.initVoiceArea();
        }, 100);
      });
    },
    // 处理微信录音结果
    processWechatRecording(localId) {
      // ("开始处理微信录音，localId:", localId);

      if (!localId) {
        console.error("localId 为空，无法进行语音识别");
        this.$showToast(this.$t('aiAssistant.recordingDataError'));
        return;
      }

      let that = this;
      this.$wx.translateVoice({
        localId: localId, // 需要识别的音频的本地Id，由录音相关接口获得
        isShowProgressTips: 1, // 默认为1，显示进度提示
        success: function (res) {
          // ("语音识别成功:", res);

          // 检查识别结果
          if (res.translateResult && res.translateResult.trim()) {
            // 添加用户语音消息
            that.messages.push({
              type: "user",
              content: res.translateResult,
            });

            // ("识别到的文本:", res.translateResult);
            that.scrollToBottom();
            that.startInterpretation(res.translateResult);
          } else {
            // ("语音识别结果为空");
            that.$showToast(that.$t('aiAssistant.noSpeechDetected'));
          }
        },
        fail: function (err) {
          console.error("语音识别失败:", err);
          that.$showToast(that.$t('aiAssistant.voiceRecognitionFailed'));
        },
      });
    },
    /**
     * 滚动聊天内容到底部
     * 功能：确保最新消息可见
     */
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContent) {
          this.$refs.chatContent.scrollTop =
            this.$refs.chatContent.scrollHeight;
        }
      });
    },
    /**
     * 处理对话框触摸移动事件
     * 功能：允许聊天内容区域滚动，阻止背景滚动
     * @param {Event} e - 触摸移动事件对象
     */
    handleDialogTouchMove(e) {
      // 如果目标元素是聊天内容区域或其子元素，允许滚动
      if (
        this.$refs.chatContent &&
        (e.target === this.$refs.chatContent ||
          this.$refs.chatContent.contains(e.target))
      ) {
        // 允许聊天内容区域滚动，不阻止默认行为
        e.stopPropagation();
      } else {
        // 阻止对话框背景区域的滚动
        e.preventDefault();
      }
    },
    /**
     * 处理对话框鼠标滚轮事件
     * 功能：允许聊天内容区域滚动，阻止背景滚动
     * @param {Event} e - 鼠标滚轮事件对象
     */
    handleDialogWheel(e) {
      // 如果目标元素是聊天内容区域或其子元素，允许滚动
      if (
        this.$refs.chatContent &&
        (e.target === this.$refs.chatContent ||
          this.$refs.chatContent.contains(e.target))
      ) {
        // 允许聊天内容区域滚动，不阻止默认行为
        e.stopPropagation();
      } else {
        // 阻止对话框背景区域的滚动
        e.preventDefault();
      }
    },
    /**
     * 处理输入框内容变化
     * 功能：更新输入文本状态，调整输入框高度
     */
    handleInput() {
      // 如果是输入法编辑过程中，不进行处理
      if (this.isComposing) {
        // ("输入法编辑中，暂不处理");
        return;
      }

      if (!this.$refs.textInput) return;

      // 获取输入内容
      const newText = this.$refs.textInput.innerText || "";

      // 检查内容是否真的为空（包括处理只有空格、换行符等情况）
      const isEmpty = !newText.trim();

      // 更新状态
      this.inputText = isEmpty ? "" : newText;

      // 如果内容为空，重置输入框
      if (isEmpty) {
        this.$refs.textInput.innerHTML = ""; // 确保DOM内容也为空
        this.resetInputArea();
        return;
      }

      // 直接调整高度，无需防抖
      this.adjustInputHeight();
    },

    /**
     * 处理输入法编辑开始事件
     * 功能：标记正在使用输入法编辑(如中文输入)
     */
    handleCompositionStart() {
      this.isComposing = true;
      // ("输入法编辑开始");
    },
    /**
     * 处理输入法编辑结束事件
     * 功能：标记输入法编辑结束，更新输入内容
     */
    handleCompositionEnd() {
      this.isComposing = false;
      // ("输入法编辑结束");

      // 输入法结束后更新内容和调整高度
      this.handleInput();
    },

    /**
     * 调整输入框高度
     * 功能：根据内容自动调整输入框高度，确保向上扩展
     */
    adjustInputHeight() {
      const textInput = this.$refs.textInput;
      if (!textInput) return;

      // 获取输入内容高度
      const scrollHeight = textInput.scrollHeight;

      // 计算行数（假设单行高度约为40px）
      const lineHeight = 40; // 单行文本的大致高度
      const lines = Math.ceil(scrollHeight / lineHeight);

      // 判断是否为多行
      const isMultiLine = lines > 1;

      // 更新多行状态
      if (isMultiLine !== this.isMultiLine) {
        this.isMultiLine = isMultiLine;
      }

      // 获取输入区域容器
      const textInputArea = textInput.closest(".text-input-area");
      const textInputContainer = textInput.closest(".text-input-container");
      if (!textInputArea || !textInputContainer) return;

      // 计算新高度
      // 如果小于等于4行，直接设置高度为内容高度+padding
      // 如果超过4行，设置为4行的高度，并启用滚动
      const maxLines = 4; // 最多显示4行
      const maxHeight = maxLines * lineHeight + 20; // 4行文本高度 + padding

      let newHeight;
      if (lines <= maxLines) {
        // 小于等于4行，高度自适应
        newHeight = Math.max(46, scrollHeight + 20);
      } else {
        // 超过4行，固定为4行高度
        newHeight = maxHeight;
      }

      // 设置输入区域高度
      textInputArea.style.height = `${newHeight}px`;

      // 调整输入包装器的对齐方式
      const inputWrapper = textInput.closest(".input-wrapper");
      if (inputWrapper) {
        if (isMultiLine) {
          // 多行时，调整为顶部对齐
          inputWrapper.style.alignItems = "flex-start";
          inputWrapper.style.paddingTop = "10px";

          // 如果超过4行，启用滚动
          if (lines > maxLines) {
            inputWrapper.style.overflowY = "auto";
            // 滚动到底部，确保光标可见
            inputWrapper.scrollTop = inputWrapper.scrollHeight;
          } else {
            inputWrapper.style.overflowY = "hidden";
          }
        } else {
          // 单行时，保持垂直居中
          inputWrapper.style.alignItems = "center";
          inputWrapper.style.paddingTop = "0";
          inputWrapper.style.paddingBottom = "0";
          inputWrapper.style.overflowY = "hidden";
        }
      }
    },

    /**
     * 重置输入区域
     * 功能：当输入为空时，重置输入框状态和样式
     */
    resetInputArea() {
      if (!this.$refs.textInput) return;

      const textInput = this.$refs.textInput;
      const inputWrapper = textInput.closest(".input-wrapper");

      // 重置状态
      this.isMultiLine = false;

      // 重置DOM
      textInput.innerHTML = "";

      // 重置包装器样式
      if (inputWrapper) {
        inputWrapper.style.alignItems = "center"; // 垂直居中
        inputWrapper.style.paddingTop = "0";
        inputWrapper.style.paddingBottom = "0";
        inputWrapper.style.overflowY = "hidden";
        inputWrapper.scrollTop = 0;
      }
    },

    /**
     * 处理文本输入区域点击事件
     * 功能：确保点击输入区域时聚焦到输入框
     * @param {Event} e - 点击事件对象
     */
    handleTextAreaClick(e) {
      // 如果点击的不是输入框本身，则手动聚焦
      if (e.target !== this.$refs.textInput && this.$refs.textInput) {
        this.$refs.textInput.focus();
      }
    },

    /**
     * 处理输入框失焦事件
     * 功能：在输入框失焦时更新状态，确保占位符正确显示
     */
    handleInputBlur() {
      this.inputFocused = false;

      // 如果输入框内容为空，确保显示占位符并更新状态
      if (this.$refs.textInput) {
        const content = this.$refs.textInput.innerText || "";

        if (!content.trim()) {
          // 更新状态
          this.inputText = "";
          this.$refs.textInput.innerHTML = ""; // 确保DOM内容也为空

          // 重置输入框
          this.resetInputArea();
        }
      }
    },

    /**
     * 处理键盘按键事件
     * 功能：监听Enter键发送消息，调整输入框高度
     * @param {Event} e - 键盘事件对象
     */
    handleKeyDown(e) {
      // 如果正在输入法编辑中，则不处理回车键
      if (e.key === "Enter" && !e.shiftKey && !this.isComposing) {
        e.preventDefault(); // 阻止默认的换行行为
        this.sendTextMessage();
      }

      // 处理退格键删除所有内容的情况
      if (e.key === "Backspace" && this.$refs.textInput) {
        // 使用setTimeout确保在DOM更新后检查内容
        setTimeout(() => {
          const content = this.$refs.textInput.innerText || "";
          if (!content.trim()) {
            // 如果内容为空，立即更新状态
            this.inputText = "";
            this.$refs.textInput.innerHTML = ""; // 确保DOM内容也为空
          }
        }, 0);
      }
    },

    /**
     * 初始化输入区域
     * 功能：设置输入区域的初始状态和高度
     */
    initInputArea() {
      if (!this.$refs.textInput) return;

      const textInput = this.$refs.textInput;
      // const textInputArea = textInput.closest(".text-input-area");
      // const inputWrapper = textInput.closest(".input-wrapper");

      // 重置状态
      this.isMultiLine = false;
      this.inputText = "";

      // 重置DOM
      textInput.innerHTML = "";

      // 重置容器样式
      // if (textInputArea) {
      // 明确设置高度为60px
      // textInputArea.style.height = "46px";
      // }

      // 重置包装器样式
      // if (inputWrapper) {
      //   inputWrapper.style.alignItems = "center";
      //   inputWrapper.style.paddingTop = "0";
      //   inputWrapper.style.paddingBottom = "0";
      //   inputWrapper.style.overflowY = "hidden"; // 重置为隐藏滚动
      //   // 确保滚动位置重置
      //   inputWrapper.scrollTop = 0;
      // }
    },

    /**
     * 初始化语音输入区域
     * 功能：确保语音输入区域的样式正确
     */
    initVoiceArea() {
      this.$nextTick(() => {
        // 使用this.$el查找组件内部的元素，而不是全局document
        const inputArea = this.$el.querySelector(".voice-input-area");
        if (inputArea) {
          // 确保高度正确
          // inputArea.style.height = "60px";
          // ("语音输入区域高度已设置为60px");
        } else {
          console.warn("未找到语音输入区域元素");
        }
      });
    },

    /**
     * 处理聊天区域滚动事件
     * 功能：监听滚动，当用户滚动到顶部时加载更多历史记录
     */
    handleScroll() {
      if (this.isLoadingHistory || !this.hasMoreHistory) return;
      if (this.inputFocused) {
        this.$refs.textInput.blur();
        this.inputFocused = false;
      }

      const scrollTop = this.$refs.chatContent.scrollTop;

      // 当滚动到顶部附近时，加载更多历史
      if (scrollTop < 50) {
        // ("滚动到顶部，加载更早的历史记录");

        // 显示加载提示
        const loadingTipElement = document.createElement("div");
        loadingTipElement.className = "history-loading-tip";
        loadingTipElement.innerHTML = this.$t('aiAssistant.loadingMore');
        loadingTipElement.style.textAlign = "center";
        loadingTipElement.style.padding = "10px 0";
        loadingTipElement.style.fontSize = "14px";
        loadingTipElement.style.color = "#999";

        if (this.$refs.chatContent) {
          this.$refs.chatContent.insertBefore(
            loadingTipElement,
            this.$refs.chatContent.firstChild
          );
        }

        // 记录当前滚动高度，用于加载后保持位置
        const oldScrollHeight = this.$refs.chatContent.scrollHeight;

        // 修改参数，加载更早的记录（顺序为升序）
        const params = {
          page: this.historyPage,
          page_size: this.historyPageSize,
          serial_number: this.packageNo,
          sort: "asc", // 按时间升序，获取更早的消息
        };

        // ("加载更早历史记录参数:", JSON.stringify(params));

        // 调用API获取历史记录
        getChatHistory(params)
          .then((response) => {
            // ("获取更早历史记录成功:", response);

            // 移除加载提示
            const loadingElement = document.querySelector(
              ".history-loading-tip"
            );
            if (loadingElement && loadingElement.parentNode) {
              loadingElement.parentNode.removeChild(loadingElement);
            }

            // 处理返回的数据
            const records =
              response?.list || response?.items || response?.records || [];

            if (records.length > 0) {
              // 转换为消息格式
              const historyMessages = records.map((item) => {
                const messageType = item.chat_type === 2 ? "user" : "ai";
                return {
                  type: messageType,
                  content: item.content || item.message || "",
                };
              });

              // 在消息列表前面添加历史记录（不包括欢迎消息）
              if (historyMessages.length > 0) {
                this.messages.splice(1, 0, ...historyMessages);
              }

              // 增加页码，继续加载下一页
              this.historyPage += 1;

              // 判断是否还有更多历史记录
              const hasNext =
                response.hasNext !== undefined
                  ? response.hasNext
                  : response.has_more !== undefined
                    ? response.has_more
                    : records.length >= this.historyPageSize;

              this.hasMoreHistory = hasNext;

            } else {
              // 没有更多数据了
              this.hasMoreHistory = false;
              // ("没有更多历史记录");
            }

            // 加载完成后恢复滚动位置
            this.$nextTick(() => {
              if (this.$refs.chatContent) {
                const newScrollHeight = this.$refs.chatContent.scrollHeight;
                const heightDiff = newScrollHeight - oldScrollHeight;
                this.$refs.chatContent.scrollTop =
                  heightDiff > 0 ? heightDiff : 0;
              }
            });
          })
          .catch((err) => {
            console.error("加载历史记录失败:", err);

            // 移除加载提示
            const loadingElement = document.querySelector(
              ".history-loading-tip"
            );
            if (loadingElement && loadingElement.parentNode) {
              loadingElement.parentNode.removeChild(loadingElement);
            }

            this.hasMoreHistory = false;
          })
          .finally(() => {
            this.isLoadingHistory = false;
          });

        // 设置加载状态
        this.isLoadingHistory = true;
      }
    },

    /**
     * 加载聊天历史记录
     * 功能：从服务器获取历史聊天记录并添加到消息列表
     */
    async loadChatHistory() {
      // 防止重复加载
      if (this.isLoadingHistory || !this.hasMoreHistory) return;

      this.isLoadingHistory = true;
      // (`开始加载历史记录，页码: ${this.historyPage}`);

      try {
        // 构建请求参数
        const params = {
          page: this.historyPage,
          page_size: this.historyPageSize,
          serial_number: this.packageNo,
          sort: "desc", // 按时间倒序，获取最新的消息
        };

        // ("发送历史记录参数:", JSON.stringify(params));

        // 调用API获取历史记录
        const response = await getChatHistory(params);
        // ("获取历史记录成功:", response);

        // 处理返回的数据
        const records =
          response?.list || response?.items || response?.records || [];

        if (records.length > 0) {
          // 转换为消息格式 - 修复类型判断逻辑
          const historyMessages = records.map((item) => {
            // 查看实际数据结构，调整判断条件
            // ("消息数据:", item);

            // 修正类型判断，根据实际返回的chat_type值进行正确映射
            // 假设chat_type为2时是用户消息，为1时是AI消息（与之前相反）
            const messageType = item.chat_type === 2 ? "user" : "ai";

            return {
              type: messageType,
              content: item.content || item.message || "",
            };
          });

          // 添加历史记录
          if (historyMessages.length > 0) {
            // 如果是第一页，直接将历史消息添加到欢迎消息之后
            // 如果不是第一页，将历史消息添加到当前消息列表的开头（不包括欢迎消息）
            if (this.historyPage === 1) {
              // 第一页，先清空除欢迎消息外的所有消息
              // this.messages = this.messages.filter((_, index) => index === 0);
              this.messages = [];

              // 添加历史消息到欢迎消息后面
              this.messages.push(...historyMessages.reverse()); // 反转确保最新的消息显示在底部
            } else {
              // 不是第一页，将历史消息添加到当前消息列表的开头

              this.messages.splice(1, 0, ...historyMessages.reverse());
            }
          }

          // 增加页码，继续加载下一页
          this.historyPage += 1;

          // 判断是否还有更多历史记录 - 适配多种可能的分页指示
          const hasNext =
            response.hasNext !== undefined
              ? response.hasNext
              : response.has_more !== undefined
                ? response.has_more
                : records.length >= this.historyPageSize;

          this.hasMoreHistory = hasNext;


        } else {
          // 没有更多数据了
          this.hasMoreHistory = false;

        }

        // 标记已加载过历史记录
        this.historyLoaded = true;
      } catch (error) {
        console.error("获取历史记录出错:", error);
        this.hasMoreHistory = false;
      } finally {
        this.isLoadingHistory = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-icon {
  position: fixed;
  right: 20px;
  width: 168px;
  height: 168px;
  z-index: 100;
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.15s ease;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: rgba(0,0,0,0) !important;
  -webkit-focus-ring-color: rgba(0,0,0,0) !important;
  tap-highlight-color: rgba(0,0,0,0) !important;
  touch-action: none;
  outline: none !important;
  will-change: transform;
  
  &, &:hover, &:focus, &:active, &:visited {
    -webkit-tap-highlight-color: rgba(0,0,0,0) !important;
    outline: none !important;
    box-shadow: none !important;
  }
  
  &.dragging {
    cursor: grabbing;
    z-index: 101;
    transition: none;
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: scale(0.98);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
    border-radius: 50%;
  }

  // // 禁用状态样式
  // &.ai-icon-disabled {
  //   cursor: not-allowed;
  //   opacity: 0.6;

  //   img {
  //     filter: grayscale(50%);
  //   }
  // }

  // 禁用状态遮罩
  // .ai-icon-mask {
  //   position: absolute;
  //   top: 0;
  //   left: 0;
  //   right: 0;
  //   bottom: 0;
  //   background: rgba(0, 0, 0, 0.3);
  //   border-radius: 50%;
  //   display: flex;
  //   align-items: center;
  //   justify-content: center;

  //   .ai-icon-tip {
  //     color: white;
  //     font-size: 24px;
  //     font-weight: 500;
  //     text-align: center;
  //     background: rgba(0, 0, 0, 0.7);
  //     padding: 8px 12px;
  //     border-radius: 20px;
  //     white-space: nowrap;
  //   }
  // }
}

.ai-chat-dialog {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  /* 调整为底部对齐 */
  justify-content: center;
  z-index: 99999999;
  animation: fadeIn 0.3s;
  touch-action: none;
  /* 防止触摸事件影响背景 */

  &.hiding {
    animation: fadeOut 0.3s forwards;

    .dialog-content {
      animation: scaleOut 0.3s forwards;
    }
  }

  .dialog-content {
    position: relative;
    width: 100%;
    height: 66.6vh;
    /* 屏幕高度的2/3 */
    max-height: calc(100% - 100px);
    background: #ffffff;
    border-radius: 30px;
    animation: scaleIn 0.3s;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #e0e0e0;
    border-radius: 30px 30px 0 0;
  }

  .close-btn {
    position: absolute;
    top: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
    z-index: 2;

    .close-icon {
      position: relative;
      width: 100%;
      height: 100%;

      &:before,
      &:after {
        content: "";
        position: absolute;
        width: 40px;
        height: 4px;
        background-color: #999;
        top: 50%;
        left: 0;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }

  .ai-header {
    background: white;
    padding: 36px 20px 26px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: none;

    .ai-avatar {
      width: 74px;
      height: 74px;
      margin-right: 12px;

      img {
        width: 100%;
        height: 100%;
      }
    }

    .ai-title-container {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 52px;
      padding-left: 30px;
    }

    .ai-title {
      display: flex;
      align-items: center;
      margin-bottom: 4px;

      .ai-name {
        font-size: 40px;
        font-weight: bold;
        color: #000;
        margin-right: 6px;
        line-height: 1.2;
      }

      .ai-sound {
        width: 40px;
        height: 40px;
        margin-left: 18px;
        // cursor: pointer;

        img {
          width: 100%;
          height: 100%;
          vertical-align: middle;
        }
      }
    }

    .ai-desc {
      font-size: 20px;
      color: #999;
      text-align: left;
      line-height: 1.2;
    }
  }

  .chat-content {
    flex: 1;
    padding: 15px 40px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* 提供iOS流畅滚动 */
    background: white;

    /* 历史加载状态 */
    .history-loading {
      text-align: center;
      padding: 10px 0;
      margin-bottom: 10px;

      .dot-countdown-timer {
        display: flex;
        justify-content: center;
        align-items: center;

        span {
          display: inline-block;
          width: 8px;
          height: 8px;
          margin: 0 3px;
          background: #ccc;
          border-radius: 50%;
          animation: dotLoading 1.4s infinite ease-in-out both;

          &:nth-child(1) {
            animation-delay: -0.32s;
          }

          &:nth-child(2) {
            animation-delay: -0.16s;
          }
        }
      }
    }

    .history-end-tip {
      text-align: center;
      color: #999;
      font-size: 24px;
      padding: 10px 0;
      margin-bottom: 10px;
    }

    .history-loading-tip {
      text-align: center;
      color: #999;
      font-size: 12px;
      padding: 10px 0;
    }

    .message-row {
      display: flex;
      margin-bottom: 15px;
      width: 100%;
    }

    .message-row-user {
      justify-content: flex-end;
    }

    .message-row-ai {
      justify-content: flex-start;
    }

    .ai-message {
      background: #eef2ff;
      border-radius: 18px;
      padding: 18px;
      font-size: 26px;
      color: #000;
      line-height: 1.6;
      display: inline-block;
      word-break: break-word;
      //max-width: 80%;
      text-align: left;

      br {
        content: "";
        display: block;
        margin-top: 10px;
      }
    }

    .user-message {
      background: #1375fb;
      color: white;
      border-radius: 18px;
      padding: 18px;
      font-size: 26px;
      line-height: 1.6;
      display: inline-block;
      word-break: break-word;
      //max-width: 80%;
      text-align: left;
    }
  }

  .chat-content:after {
    content: "";
    height: 200px;

    width: 100%;
    display: block;
  }

  .chat-footer {
    //height: auto;
    //min-height: 130px;
    padding: 0 25px;
    height: 200px;
    //line-height: 200px;
    border-top: none;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    //position: relative;
    //background: pink;
    box-sizing: border-box;
    position: fixed;
    padding-bottom: 40px;
    bottom: 0;
    width: 100%;

    .recording-status {
      text-align: center;
      color: #666;
      font-size: 24px;
      margin-bottom: 40px;
      height: 24px;

      span {
        color: #666;

        &.cancel {
          color: #666;
        }
      }
    }
  }

  .voice-input-area {
    flex: 1;
    background: white;
    border: 1px solid #e5e9f2;
    border-radius: 30px;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    height: 80px !important;
    /* 强制固定高度 */
    max-height: 80px !important;
    /* 确保最大高度 */
    outline: none;
    -webkit-tap-highlight-color: transparent;

    &.recording {
      background: #1375fb;
      height: 80px !important;
      /* 强制固定高度 */
      max-height: 80px !important;
      /* 确保最大高度 */
      border-radius: 30px;
      border-color: #1375fb;
      box-shadow: none;

      .keyboard-toggle {
        display: none;
      }

      .voice-button span {
        color: white;
      }
    }

    .voice-button {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      -webkit-touch-callout: none;
      outline: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      /* 优化触摸响应 */
      position: relative;
      z-index: 1;

      /* 确保所有子元素都不会阻止事件传递 */
      * {
        pointer-events: none;
      }

      .voice-content {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        pointer-events: none;

        .voice-text {
          font-size: 30px;
          color: #333;
          font-weight: normal;
          text-align: center;
          pointer-events: none;
        }
      }

      span {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none;

        .voice-icon {
          width: 50px;
          height: 50px;
          margin-bottom: 5px;
          pointer-events: none;
        }

        //.voice-text {
        //  font-size: 22px;
        //  color: #333;
        //  font-weight: normal;
        //  text-align: center;
        //  pointer-events: none;
        //}
      }

      .recording-indicator {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 260px;
        width: 100%;
        pointer-events: none;

        * {
          pointer-events: none;
        }

        // .countdown-timer {
        //   color: white;
        //   font-size: 24px;
        //   font-weight: normal;
        //   text-align: center;
        // }
      }
    }
  }

  .keyboard-toggle {
    width: 46px;
    height: 46px;
    position: absolute;
    right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    cursor: pointer;
    background-color: transparent;
    border-radius: 50%;
    top: 0;
    bottom: 0;
    margin: auto;
    transform: none;

    &:active {
      background-color: rgba(0, 0, 0, 0.05);
    }

    img {
      width: 50px;
      height: 50px;
      opacity: 0.8;
      transition: opacity 0.2s;
      pointer-events: none;
    }
  }

  .text-input-container {
    height: auto;
    min-height: 90px;
    // padding: 20px 25px;
    position: relative;
    background: white;
    display: flex;
    align-items: flex-end;
    /* 将内容底部对齐 */
  }

  .text-input-area {
    position: relative;
    height: 90px;
    min-height: 90px;
    max-height: 180px;
    /* 约等于4行文本的高度 */
    border: 1px solid #e5e9f2;
    border-radius: 30px;
    background: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
    display: flex;
    padding: 0;
    overflow: visible;
    transition: height 0.15s ease;
    width: 100%;
    /* 确保宽度100% */
    margin-bottom: 0;
    /* 确保底部没有边距 */
  }

  .input-wrapper {
    flex: 1;
    padding: 0 20px;
    padding-right: 70px;
    height: 100%;
    display: flex;
    align-items: center;
    /* 保持垂直居中 */
    box-sizing: border-box;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    -webkit-overflow-scrolling: touch;

    /* 隐藏滚动条但保留滚动功能 */
    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .auto-grow-input {
    width: 100%;
    min-height: 40px;
    border: none;
    outline: none;
    background: transparent;
    font-size: 26px;
    color: #333;
    font-weight: 500;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
    caret-color: #1375fb;
    display: flex;
    align-items: center;

    /* 隐藏滚动条 */
    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;

    &:empty:before {
      content: attr(placeholder);
      color: #999;
      position: absolute;
      pointer-events: none;
      font-weight: 400;
      left: 20px;
      top: 50%;
      transform: translateY(-50%);
    }

    &:focus {
      outline: none;
    }
  }

  .input-action {
    width: 70px;
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 100;
    border-radius: 50%;
    background-color: transparent;

    &:active {
      background-color: rgba(0, 0, 0, 0.05);
    }

    img {
      width: 50px;
      height: 50px;
      opacity: 0.8;
      transition: opacity 0.2s;
      pointer-events: none;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes scaleIn {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

@keyframes scaleOut {
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
}

@keyframes dotLoading {

  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}

/* 思考动画样式 */
.ai-message.thinking-message {
  padding: 18px;
  min-width: 80px;
}

.thinking-dots {
  display: flex;
  justify-content: center;
  align-items: center;
}

.thinking-dots span {
  display: inline-block;
  width: 12px;
  /* 增大点的尺寸 */
  height: 12px;
  margin: 0 5px;
  background-color: #4285f4;
  /* 使用更明显的颜色 */
  border-radius: 50%;
  animation: thinking 1.4s infinite ease-in-out both;
  box-shadow: 0 0 5px rgba(66, 133, 244, 0.5);
  /* 添加发光效果 */
}

.ai-message.thinking-message {
  background-color: #f0f4fe;
  /* 思考消息的背景色 */
  border: 1px dashed #d0d9ff;
  /* 添加虚线边框 */
}

@keyframes thinking {

  0%,
  100% {
    transform: scale(0.4);
    opacity: 0.3;
  }

  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

.countdown-timer {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.countdown-timer .span {
  background: #fff;
  width: 7px;
  height: 10%;
  border-radius: 14px;
  margin-right: 5px;
}

.countdown-timer .span:last-child {
  margin-right: 0px;
}

.countdown-timer .span:nth-child(1) {
  animation: load 2.5s 1.4s infinite linear;
}

.countdown-timer .span:nth-child(2) {
  animation: load 2.5s 1.2s infinite linear;
}

.countdown-timer .span:nth-child(3) {
  animation: load 2.5s 1s infinite linear;
}

.countdown-timer .span:nth-child(4) {
  animation: load 2.5s 0.8s infinite linear;
}

.countdown-timer .span:nth-child(5) {
  animation: load 2.5s 0.6s infinite linear;
}

.countdown-timer .span:nth-child(6) {
  animation: load 2.5s 0.4s infinite linear;
}

.countdown-timer .span:nth-child(7) {
  animation: load 2.5s 0.2s infinite linear;
}

.countdown-timer .span:nth-child(8) {
  animation: load 2.5s 0s infinite linear;
}

.countdown-timer .span:nth-child(9) {
  animation: load 2.5s 0.2s infinite linear;
}

.countdown-timer .span:nth-child(10) {
  animation: load 2.5s 0.4s infinite linear;
}

.countdown-timer .span:nth-child(11) {
  animation: load 2.5s 0.6s infinite linear;
}

.countdown-timer .span:nth-child(12) {
  animation: load 2.5s 0.8s infinite linear;
}

.countdown-timer .span:nth-child(13) {
  animation: load 2.5s 1s infinite linear;
}

.countdown-timer .span:nth-child(14) {
  animation: load 2.5s 1.2s infinite linear;
}

.countdown-timer .span:nth-child(15) {
  animation: load 2.5s 1.4s infinite linear;
}

@keyframes load {
  0% {
    background: #fff;
    margin-top: 0%;
    height: 10%;
  }

  50% {
    background: #fff;
    height: 20%;
    margin-top: 0%;
  }

  100% {
    background: #fff;
    height: 10%;
    margin-top: 0%;
  }
}

.seconds-font {
  font-size: 24px;
}
</style>
